<template>
  <main id="app" class="app">
    <Navbar v-if="!framed" :logo-hidden="logoHidden">
      <component :is="leftNavbarComponent" @click.native="handleLeftNavbarLeftClick" slot="left" />
      <component :is="rightNavbarComponent" @click.native="handleNavbarRightClick" slot="right" />
    </Navbar>
    <TransitionRouterView
      :aria-hidden="modalOpen || dialogOpen"
      @subview-event="handleSubviewEvent"
    ></TransitionRouterView>
    <Tabbar :aria-hidden="modalOpen || dialogOpen" class="app__tabbar" v-if="!framed"></Tabbar>
    <div class="app__loader">
      <div class="app__loader-inner" :class="{'loading': loading, 'loaded': loaded}"></div>
    </div>
    <DialogVC></DialogVC>
    <HUIToastContainer
      class="app__toast-container"
      @toast-close="handleToastClose"
      :toasts="toasts"
    ></HUIToastContainer>
  </main>
</template>

<script>
import { mapState } from 'vuex'
import { HUIToastContainer, HUINavbarButtonBack } from '@/halifax-ui'
import Tabbar from '@/containers/Tabbar'
import Navbar from '@/containers/Navbar'
import DialogVC from '@/containers/DialogVC'
import TransitionRouterView from '@/containers/TransitionRouterView'
import subscribesToWindowHorizontalResize from '@/mixins/subscribesToWindowHorizontalResize'

export default {
  mixins: [subscribesToWindowHorizontalResize],
  components: {
    Tabbar,
    HUIToastContainer,
    DialogVC,
    Navbar,
    TransitionRouterView
  },
  data() {
    return {
      offlineToastId: null,
      loading: false,
      loaded: false,
      leftNavbarComponent: null,
      rightNavbarComponent: null,
      logoHidden: false
    }
  },
  computed: {
    ...mapState({
      offline: 'offline',
      modalOpen: 'modal_open'
    }),
    ...mapState('routing', {
      navigating: 'navigating'
    }),
    ...mapState('dialog', {
      dialogOpen: 'visible'
    }),
    ...mapState('toast', {
      toasts: 'items'
    }),
    ...mapState({
      platform: 'platform',
      framed: 'framed'
    })
  },
  watch: {
    $route() {
      this.updateNavbarComponents()
      this.trackPageview(this.$route)
    },
    navigating(navigationActive) {
      if (navigationActive) {
        // TRUE = navigation started

        clearTimeout(this.timeout)
        this.loaded = false
        this.loading = true
      } else {
        // FALSE = navigation done
        this.loaded = true
        this.loading = false

        this.timeout = setTimeout(() => {
          this.loaded = false
          this.loading = false
        }, 2500)
      }
    },
    modalOpen(open) {
      if (open) {
        document.documentElement.classList.add('modal-open')
      } else {
        document.documentElement.classList.remove('modal-open')
      }
    },
    dialogOpen(open) {
      if (open) {
        document.documentElement.classList.add('dialog-open')
      } else {
        document.documentElement.classList.remove('dialog-open')
      }
    },
    async offline(offline) {
      if (offline) {
        this.offlineToastId = await this.$store.dispatch('toast/SHOW', {
          text: 'Du har ikke forbindelse til internettet.',
          theme: 'red',
          closeable: false,
          lifetime: Infinity
        })
      } else {
        if (this.offlineToastId !== null) {
          this.$store.dispatch('toast/REMOVE', { id: this.offlineToastId })
          this.offlineToastId = null
        }
      }
    }
  },
  created() {
    this.$store.dispatch('menu/FETCH')
    this.$store.dispatch('restaurant/FETCH_ALL')

    //Dispatched from the ios wrapper
    window.addEventListener('applicationDidBecomeActive', this.handlePageVisibilityChange)
    window.addEventListener('visibilitychange', this.handlePageVisibilityChange)
    window.addEventListener('online', this.handleNavigatorConnectivityChange)
    window.addEventListener('offline', this.handleNavigatorConnectivityChange)
    this.handleNavigatorConnectivityChange()

    document.documentElement.classList.add('app--mounted')
  },
  mounted() {
    this.updateNavbarComponents()
    this.trackPageview(this.$route)
  },
  methods: {
    handleNavigatorConnectivityChange() {
      this.$store.dispatch('SET_OFFLINE', navigator.onLine === false ? true : false)
    },
    handleToastClose(id) {
      this.$store.dispatch('toast/REMOVE', { id })
    },
    handlePageVisibilityChange() {
      if (!document.hidden) {
        this.$bus.$emit('APP_DID_BECOME_ACTIVE')
      }
    },
    onWindowHorizontalResize(innerWidth, innerHeight) {
      if (this.platform === 'browser') {
        // Update the windowInnerHeight property - see head.js to see what it does
        // Only relevant in browser since ios and android is fullscreen
        document.documentElement.style.setProperty('--windowInnerHeight', innerHeight + 'px')
      }
    },
    updateNavbarComponents() {
      const { leftNavbarComponent, rightNavbarComponent, navbarLogoHidden } = this.$route.meta

      this.logoHidden = navbarLogoHidden

      if (typeof leftNavbarComponent !== 'undefined') {
        this.leftNavbarComponent = leftNavbarComponent
      } else if (window.history.length > 1) {
        this.leftNavbarComponent = HUINavbarButtonBack
      }

      if (typeof rightNavbarComponent !== 'undefined') {
        this.rightNavbarComponent = rightNavbarComponent
      } else {
        this.rightNavbarComponent = null
      }
    },
    handleLeftNavbarLeftClick() {
      const { leftNavbarComponentAction } = this.$route.meta

      if (typeof leftNavbarComponentAction === 'function') {
        leftNavbarComponentAction(this.$route)
      } else {
        this.$router.go(-1)
      }
    },
    handleNavbarRightClick() {
      const { rightNavbarComponentAction } = this.$route.meta

      if (typeof rightNavbarComponentAction === 'function') {
        rightNavbarComponentAction(this.$route)
      }
    },
    handleSubviewEvent() {},
    trackPageview(route) {
      const meta = route.meta || {}
      const customTracking = meta.customTracking || {}

      // If there is a customTracking configuration, track that path
      const path = customTracking.path || location.pathname

      // Construct a query string with key value pairs for each of the custom params
      const customParamsSearch = (customTracking.params || []).reduce((acc, curr) => {
        if (acc) {
          acc += '&'
        }
        if (route.params[curr]) {
          acc += `${curr}=${route.params[curr]}`
        }
        return acc
      }, '')

      // Concatenate the custom params with any existing params in location.search
      const search = location.search
        ? `${location.search}&${customParamsSearch}`
        : customParamsSearch
        ? `?${customParamsSearch}`
        : ''

      if (window.ga) {
        ga('send', 'pageview', `${path}${search}`)
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';

@import '~@/scss/reset';
@import '~@/scss/fonts';
@import '~@/halifax-ui/dist/halifax-ui.css';

@import '~@/scss/atoms/a-container';
@import '~@/scss/atoms/a-page-title';
@import '~@/scss/atoms/a-page-subtitle';
@import '~@/scss/atoms/a-summary-dl';
@import '~@/scss/atoms/a-button-group';
@import '~@/scss/atoms/a-order-buttons';
@import '~@/scss/atoms/a-rich-text';
@import '~@/scss/atoms/a-bordered-fieldset';

:root {
  --safe-area-inset-top: env(safe-area-inset-top);
  --safe-area-inset-bottom: env(safe-area-inset-bottom);
}

html {
  line-height: 1.4;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-family: $font-family;
  font-weight: 500;
  background-color: $background-color;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  max-width: 100vw;
  overflow-x: hidden;

  // head.js adds window--callback if callback=1 is set in the query string
  // This is the case when the iframe is redirected by quickpay.
  &.window--callback {
    background-color: #fff;
  }
  &.app--mounted {
    // iOS Safari overscrolls, so make sure that all the area that we can possibly
    // overscroll is the same color as the tabbar
    &::after {
      content: '';
      display: block;
      position: fixed;
      bottom: 0;
      transform: translate3d(0, 0, 0);
      width: 100%;
      z-index: -1;
      height: 50%;
      background: $background-color-tabbar;
    }
  }
}

body {
  color: $text-color;
  font-feature-settings: 'kern' 1, 'liga' 1;
  min-height: 100vh;
  min-height: var(--windowInnerHeight, 100vh);

  .app--mounted & {
    background-color: $background-dark;
  }
  .no-touch.modal-open &,
  .no-touch.dialog-open & {
    overflow: hidden;
  }
  .touch.modal-open &,
  .touch.dialog-open & {
    touch-action: none;
  }
}
.platform--ios * {
  -webkit-touch-callout: none;
}
.platform--ios *:not(input) {
  user-select: none;
}
button {
  font-weight: 500;
  user-select: none;
}
a {
  color: $text-color;
}
h1 {
  font-size: 3.2rem;
}
h2 {
  font-size: 2rem;
}
h3 {
  font-size: 1.6rem;
}
hr {
  border: none;
  height: 1px;
  background-color: currentColor;
  opacity: 0.2;
  margin: 0;
}

// This will hide the focus indicator if the element receives focus via the mouse,
// but it will still show up on keyboard focus.
.js-focus-visible :focus:not(.focus-visible) {
  outline: none;
}

// Optionally: Define a strong focus indicator for keyboard focus. If you choose to
// skip this step then the browser's default focus indicator will be displayed instead.
.js-focus-visible .focus-visible {
  outline: $hui-default-outline;
  -moz-outline-radius: 8px;
}

.app {
  overflow: hidden;

  &__toast-container {
    bottom: $height-tabbar;
    z-index: $z-index-toasts;
    transition: margin 200ms, bottom 200ms;
    transition-delay: 200ms;

    @include supports-safe-area-insets {
      margin-bottom: var(--safe-area-inset-bottom);
    }

    .modal-open & {
      bottom: 0;
      margin-bottom: 0;
    }
  }
  &__loader {
    position: fixed;
    bottom: $height-tabbar - 2px;
    height: 2px;
    padding: 3px 0;
    width: 100%;
    overflow: hidden;
    z-index: $z-index-tabbar;
    pointer-events: none;
    box-sizing: content-box;

    @include supports-safe-area-insets {
      bottom: calc(#{$height-tabbar} + var(--safe-area-inset-bottom) - 3px - 2px);
    }
    .app--tabbar-hidden & {
      bottom: 0;
      padding-bottom: 0;
    }
  }
  &__loader-inner {
    $base-delay: 300ms;
    $loaded-duration: 150ms;
    $loading-duration: 5000ms;

    width: 100%;
    height: 100%;
    box-sizing: border-box;
    background: $halifax-orange;
    will-change: opacity, transform;
    backface-visibility: hidden;
    transform: translate3d(-100%, 0, 0);
    transition: transform 0ms linear, opacity 100ms linear;
    transition-delay: 0ms, 0ms;
    opacity: 0;

    &.loading {
      transition-duration: $loading-duration, 0ms;
      transform: translate3d(-1px, 0, 0);
      transition-delay: $base-delay;
      opacity: 1;
    }
    &.loaded {
      transform: translate3d(0, 0, 0);
      opacity: 0;
      transition-duration: $loaded-duration, 0ms;
      transition-delay: $base-delay, $base-delay + $loaded-duration + 100ms;
    }
  }
}
</style>
